import { useMemo } from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

// Stella returns a small, library-agnostic chart payload
// ({ type, title, labels, series }). This maps that payload onto the Highcharts
// options the admin already uses elsewhere, so a chart renders inline with the
// assistant reply without teaching the model anything about Highcharts.
const PALETTE = ['#3b82f6', '#0d6efd', '#22c55e', '#f59e0b', '#a855f7']

const longestLabelLength = (labels) =>
  labels.reduce((max, label) => Math.max(max, String(label || '').length), 0)

const buildOptions = (chart) => {
  const labels = Array.isArray(chart?.labels) ? chart.labels : []
  const series = Array.isArray(chart?.series) ? chart.series : []
  const wantsHorizontal =
    chart?.type === 'bar' &&
    (labels.some((label) => String(label || '').length > 14) ||
      longestLabelLength(labels) > 12)
  const highchartsType =
    chart?.type === 'line'
      ? 'line'
      : chart?.type === 'pie'
        ? 'pie'
        : wantsHorizontal
          ? 'bar'
          : 'column'

  const base = {
    chart: {
      type: highchartsType,
      height: wantsHorizontal ? Math.max(280, 36 * labels.length + 80) : 280,
      backgroundColor: 'transparent',
      spacing: [8, 16, 8, 8],
      style: { fontFamily: 'inherit' },
    },
    title: { text: undefined },
    credits: { enabled: false },
    colors: PALETTE,
    legend: { enabled: series.length > 1 || highchartsType === 'pie' },
    tooltip: { valueDecimals: 0 },
  }

  if (highchartsType === 'pie') {
    const source = series[0]?.data || []
    return {
      ...base,
      series: [
        {
          name: series[0]?.name || 'Total',
          data: source.map((value, index) => ({
            name: labels[index] || `Item ${index + 1}`,
            y: value,
          })),
        },
      ],
      plotOptions: {
        pie: {
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            style: { color: '#374151', fontSize: '11px' },
          },
        },
      },
    }
  }

  const categoryAxis = {
    categories: labels,
    lineColor: '#e5e7eb',
    tickColor: '#e5e7eb',
    labels: {
      rotation: 0,
      style: { color: '#6b7280', fontSize: '11px' },
    },
    title: { text: chart?.categoryTitle || undefined },
  }
  const valueAxis = {
    min: 0,
    title: { text: chart?.valueTitle || series[0]?.name || null },
    gridLineColor: '#eef0f2',
    labels: { style: { color: '#6b7280', fontSize: '11px' } },
  }

  return {
    ...base,
    xAxis: categoryAxis,
    yAxis: valueAxis,
    plotOptions: {
      bar: {
        borderWidth: 0,
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
      },
      column: {
        borderWidth: 0,
        pointPadding: 0.1,
        groupPadding: 0.12,
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
      },
      line: { marker: { radius: 3 }, dataLabels: { enabled: true } },
    },
    series: series.map((entry, index) => ({
      name: entry?.name || `Series ${index + 1}`,
      data: Array.isArray(entry?.data) ? entry.data : [],
    })),
  }
}

export default function StellaChart({ chart }) {
  const options = useMemo(() => buildOptions(chart), [chart])

  const hasData =
    Array.isArray(chart?.series) &&
    chart.series.some(
      (entry) => Array.isArray(entry?.data) && entry.data.length
    )

  if (!hasData) return null

  return (
    <figure className="draft-events-assistant__chart">
      {chart?.title ? (
        <figcaption className="draft-events-assistant__chart-title">
          {chart.title}
        </figcaption>
      ) : null}
      <HighchartsReact highcharts={Highcharts} options={options} />
    </figure>
  )
}
